import type { Issue, IssueStatus } from '@/types/issue';
import type { SharedCompany } from '@/types/shared';
import type { Team } from '@/types/team';
import type { CurrentUser } from '@/types/user';

export const ISSUE_STATUSES: IssueStatus[] = [
  'in_progress',
  'todo',
  'in_review',
  'backlog',
  'done',
  'canceled',
  'duplicate',
];

export const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: 'Backlog',
  todo: 'Todo',
  in_progress: 'In Progress',
  in_review: 'In Review',
  done: 'Done',
  canceled: 'Canceled',
  duplicate: 'Duplicate',
};

export const ACTIVE_STATUSES: IssueStatus[] = ['todo', 'in_progress', 'in_review'];

export const PRIORITY_LABELS: Record<number, string> = {
  0: 'No priority',
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Low',
};

const WORKSPACE_COLORS = [
  '#5e6ad2',
  '#26a544',
  '#f2c94c',
  '#eb5757',
  '#26b5ce',
  '#bb87fc',
  '#ff7222',
];

export function colorFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return WORKSPACE_COLORS[Math.abs(hash) % WORKSPACE_COLORS.length];
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function placeholderTeams(company: SharedCompany | null): Team[] {
  const name = company?.name ?? 'Engineering';
  const key =
    name
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 3)
      .toUpperCase() || 'ENG';

  return [
    {
      id: 'placeholder-engineering',
      name: 'Engineering',
      key: key.slice(0, 5) || 'ENG',
      color: company ? colorFromId(company.id) : '#5e6ad2',
    },
  ];
}

export function resolveTeams(teams: Team[] | undefined, _company: SharedCompany | null): Team[] {
  if (teams && teams.length > 0) return teams;
  return [];
}

export function fallbackUser(): CurrentUser {
  return {
    id: 'local',
    name: 'Lance Foley',
    emailAddress: 'lance.foley@caprocklabs.com',
    avatarColor: '#5e6ad2',
    emailNotifications: true,
  };
}

export function groupIssuesByStatus(
  issues: Issue[]
): Array<{ status: IssueStatus; issues: Issue[] }> {
  return ISSUE_STATUSES.map((status) => ({
    status,
    issues: issues.filter((issue) => issue.status === status),
  })).filter((group) => group.issues.length > 0);
}

export type IssueGroupBy = 'status' | 'assignee' | 'priority' | 'project' | 'none';
export type IssueOrder = 'updated' | 'created' | 'priority' | 'title';

export interface IssueGroup {
  key: string;
  label: string;
  issues: Issue[];
}

export function sortIssues(issues: Issue[], order: IssueOrder): Issue[] {
  const copy = [...issues];
  if (order === 'title') {
    copy.sort((a, b) => a.title.localeCompare(b.title));
  } else if (order === 'priority') {
    copy.sort((a, b) => {
      const av = a.priority === 0 ? 99 : a.priority;
      const bv = b.priority === 0 ? 99 : b.priority;
      return av - bv;
    });
  } else if (order === 'created') {
    copy.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } else {
    copy.sort((a, b) => (b.updatedAt || b.createdAt).localeCompare(a.updatedAt || a.createdAt));
  }
  return copy;
}

export function groupIssues(issues: Issue[], groupBy: IssueGroupBy): IssueGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'Issues', issues }];
  }

  if (groupBy === 'status') {
    return groupIssuesByStatus(issues).map((group) => ({
      key: group.status,
      label: STATUS_LABELS[group.status],
      issues: group.issues,
    }));
  }

  if (groupBy === 'priority') {
    return [1, 2, 3, 4, 0]
      .map((priority) => ({
        key: String(priority),
        label: PRIORITY_LABELS[priority],
        issues: issues.filter((issue) => issue.priority === priority),
      }))
      .filter((group) => group.issues.length > 0);
  }

  if (groupBy === 'project') {
    const map = new Map<string, IssueGroup>();
    issues.forEach((issue) => {
      const key = issue.projectId ?? issue.project?.id ?? 'none';
      const label = issue.project?.name ?? 'No project';
      const existing = map.get(key);
      if (existing) {
        existing.issues.push(issue);
      } else {
        map.set(key, { key, label, issues: [issue] });
      }
    });
    return Array.from(map.values());
  }

  const map = new Map<string, IssueGroup>();
  issues.forEach((issue) => {
    const key = issue.assigneeId ?? issue.assignee?.id ?? 'unassigned';
    const label = issue.assignee?.name ?? 'Unassigned';
    const existing = map.get(key);
    if (existing) {
      existing.issues.push(issue);
    } else {
      map.set(key, { key, label, issues: [issue] });
    }
  });
  return Array.from(map.values());
}
